import { useMutation, useQueryClient } from '@tanstack/react-query';
import GraphqlService from '../service/graphqlService';
import { useSnackbar } from '../providers/SnackbarContext';
import { useAuditLog, AUDIT_ENTITY_DEVICE_DEFENDER, AUDIT_CHANGE_DEVICE_UNBLOCKED } from './useAuditLog';

/**
 * Hook for releasing (unblocking) a device from Device Defender quarantine
 * Includes audit logging on successful release
 * 
 * @param {Object} options - Hook options
 * @param {Function} [options.onSuccess] - Callback on successful release
 * @param {Function} [options.onError] - Callback on error
 * @returns {Object} Mutation result with releaseDevice function
 */
const useReleaseDevice = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();
  const openSnackBar = useSnackbar();
  const { logAuditChange } = useAuditLog({ silent: true });

  const mutation = useMutation(
    ({ projectId, deviceSerial }) => GraphqlService.releaseDevice({ projectId, deviceSerial }),
    {
      onSuccess: (data, variables) => {
        // Invalidate quarantine status cache to trigger refetch
        queryClient.invalidateQueries(['deviceQuarantineStatus', variables.projectId, variables.deviceSerial]);
        
        // Log audit change
        logAuditChange(
          AUDIT_ENTITY_DEVICE_DEFENDER,
          AUDIT_CHANGE_DEVICE_UNBLOCKED,
          variables.deviceSerial,
          `Device released in project ${variables.projectId}`,
          variables.deviceSerial
        );

        openSnackBar('Device released successfully', 'success');
        
        if (onSuccess) {
          onSuccess(data, variables);
        }
      },
      onError: (error, variables) => {
        console.error('[useReleaseDevice] Error:', error);
        openSnackBar(`Failed to release device: ${error.message}`, 'error');
        
        if (onError) {
          onError(error, variables);
        }
      },
    }
  );

  return {
    releaseDevice: mutation.mutate,
    releaseDeviceAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
};

export default useReleaseDevice;
